import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Payment,
  PaymentVm,
  PaymentListResponse,
  VendorBill,
  VendorAdvanceForPayment,
  VendorAdvanceLine,
  PaymentAccount,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Purchase Payments management service
 * Handles all payment-related API operations
 */
export class PaymentsService {
  /**
   * Get paginated list of payments for a branch
   */
  async getAllPayments(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<PaymentListResponse> {
    const url = appendSortDir(endpoints.payments.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<PaymentListResponse> | PaymentListResponse | any>(url)
    const payload = response.data

    // Helper to map payments array
    const mapPayments = (payments: any[]): Payment[] => {
      return payments.map(pay => this.mapPaymentData(pay))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapPayments(data),
          }
        }
        return data as PaymentListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapPayments(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapPayments(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapPayments(payload.data) : [],
    }
  }

  /**
   * Get payment by ID
   */
  async getPaymentById(branchId: number, id: number): Promise<Payment> {
    const url = endpoints.payments.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Payment> | Payment | any>(url)
    
    let paymentData: any
    
    if ('data' in response.data && 'success' in response.data) {
      paymentData = response.data.data
    } else if ('data' in response.data) {
      paymentData = response.data.data
    } else {
      paymentData = response.data
    }
    
    return this.mapPaymentData(paymentData)
  }

  /**
   * Get all bills for a branch
   */
  async getAllBills(branchId: number): Promise<VendorBill[]> {
    const url = endpoints.payments.getAllBills(branchId)
    const response = await apiClient.get<ApiResponse<VendorBill[]> | VendorBill[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get vendor bills
   */
  async getVendorsBills(vendorId: number): Promise<VendorBill[]> {
    const url = endpoints.payments.getVendorsBills(vendorId)
    const response = await apiClient.get<ApiResponse<VendorBill[]> | VendorBill[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get all vendor advances for a branch
   */
  async getAllAdvance(branchId: number): Promise<VendorAdvanceForPayment[]> {
    const url = endpoints.payments.getAllAdvance(branchId)
    const response = await apiClient.get<ApiResponse<VendorAdvanceForPayment[]> | VendorAdvanceForPayment[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get vendor advances
   * @param vendorId - Vendor ID
   * @param advanceId - Advance ID
   */
  async getVendorsAdvance(vendorId: number, advanceId: number): Promise<VendorAdvanceForPayment[]> {
    const url = endpoints.payments.getVendorsAdvance(vendorId, advanceId)
    const response = await apiClient.get<ApiResponse<VendorAdvanceForPayment[]> | VendorAdvanceForPayment[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get vendor advance lines by advance ID
   */
  async getVendorAdvanceLineByAdvanceId(advanceId: number): Promise<VendorAdvanceLine[]> {
    const url = endpoints.payments.getVendorAdvanceLineByAdvanceId(advanceId)
    const response = await apiClient.get<ApiResponse<VendorAdvanceLine[]> | VendorAdvanceLine[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  async getVendorAdvanceLinesByPaymentId(paymentId: number): Promise<VendorAdvanceLine[]> {
    const url = endpoints.payments.getVendorAdvanceLinesByPaymentId(paymentId)
    const response = await apiClient.get<ApiResponse<VendorAdvanceLine[]> | VendorAdvanceLine[] | any>(url)

    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get payment lines by payment ID
   * Returns array of payment line items containing bill IDs
   */
  async getPaymentLinesByPaymentId(paymentId: number): Promise<any[]> {
    const url = endpoints.payments.getPaymentLinesByPaymentId(paymentId)
    const response = await apiClient.get<ApiResponse<any[]> | any[] | any>(url)
    
    console.log('🔵 [PaymentsService.getPaymentLinesByPaymentId]', {
      paymentId,
      url,
      responseData: response.data,
      responseDataType: typeof response.data,
      isArray: Array.isArray(response.data),
    })
    
    // Handle different response structures
    if (Array.isArray(response.data)) {
      console.log('🔵 Payment lines (direct array):', response.data)
      return response.data
    }
    
    if (response.data && typeof response.data === 'object') {
      if ('data' in response.data && Array.isArray(response.data.data)) {
        console.log('🔵 Payment lines (nested data):', response.data.data)
        return response.data.data
      }
      
      if ('success' in response.data && 'data' in response.data && Array.isArray(response.data.data)) {
        console.log('🔵 Payment lines (success wrapper):', response.data.data)
        return response.data.data
      }
    }
    
    console.warn('🔵 No payment lines found or unexpected response structure')
    return []
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<PaymentAccount[]> {
    const url = endpoints.payments.getAllAccounts(branchId)
    const response = await apiClient.get<ApiResponse<PaymentAccount[]> | PaymentAccount[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get user IP address
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Insert payment
   */
  async insertPayment(payment: PaymentVm): Promise<Payment> {
    const url = endpoints.payments.insert
    
    // Get IP address if not provided
    const paymentWithIP = {
      ...payment,
      ipAddress: payment.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('%c=== PaymentsService.insertPayment ===', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%cPayload (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(paymentWithIP, null, 2))
    console.log('%cPayload (Object):', 'color: #009688; font-weight: bold;', paymentWithIP)
    console.log('%c=====================================', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.post<ApiResponse<Payment> | Payment>(url, paymentWithIP)
    
    console.log('%c=== API Response ===', 'background: #607D8B; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cResponse Status:', 'color: #4CAF50; font-weight: bold;', response.status)
    console.log('%cResponse Data (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(response.data, null, 2))
    console.log('%cResponse Data (Object):', 'color: #009688; font-weight: bold;', response.data)
    console.log('%c====================', 'background: #607D8B; color: white; padding: 5px; font-weight: bold;')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapPaymentData((response.data as ApiResponse<Payment>).data)
    }
    
    return this.mapPaymentData(response.data as Payment)
  }

  /**
   * Update payment
   */
  async updatePayment(payment: PaymentVm): Promise<Payment> {
    const url = endpoints.payments.update
    
    // Get IP address if not provided
    const paymentWithIP = {
      ...payment,
      ipAddress: payment.ipAddress || await this.getUserIPAddress(),
    }
    
    console.log('%c=== PaymentsService.updatePayment ===', 'background: #FF9800; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cEndpoint:', 'color: #2196F3; font-weight: bold;', url)
    console.log('%cPayload (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(paymentWithIP, null, 2))
    console.log('%cPayload (Object):', 'color: #009688; font-weight: bold;', paymentWithIP)
    console.log('%c====================================', 'background: #FF9800; color: white; padding: 5px; font-weight: bold;')
    
    const response = await apiClient.put<ApiResponse<Payment> | Payment>(url, paymentWithIP)
    
    console.log('%c=== API Response ===', 'background: #607D8B; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
    console.log('%cResponse Status:', 'color: #4CAF50; font-weight: bold;', response.status)
    console.log('%cResponse Data (JSON):', 'color: #9C27B0; font-weight: bold;', JSON.stringify(response.data, null, 2))
    console.log('%cResponse Data (Object):', 'color: #009688; font-weight: bold;', response.data)
    console.log('%c====================', 'background: #607D8B; color: white; padding: 5px; font-weight: bold;')
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapPaymentData((response.data as ApiResponse<Payment>).data)
    }
    
    return this.mapPaymentData(response.data as Payment)
  }

  async deletePayment(id: number, branchId: number): Promise<unknown> {
    const url = endpoints.payments.delete(id, branchId)
    const response = await apiClient.delete(url, { skipHandledLocallyErrorUI: true } as any)
    return response.data
  }

  /**
   * Map API response to Payment interface
   */
  private mapPaymentData(paymentData: any): Payment {
    return {
      paymentId: paymentData.paymentId || paymentData.id || 0,
      paymentNo: paymentData.paymentNo || paymentData.paymentNumber || '',
      paymentDate: paymentData.paymentDate || '',
      billId: paymentData.billId || 0,
      billNo: paymentData.billNo || paymentData.billNumber || '',
      vendorId: paymentData.vendorId || 0,
      vendorName: paymentData.vendorName || paymentData.vendorNameEn || paymentData.vendorNameAr || '',
      vendorNameEn: paymentData.vendorNameEn || paymentData.vendorName || '',
      vendorNameAr: paymentData.vendorNameAr || '',
      refNo: paymentData.refNo || paymentData.refNumber || '',
      paymentType: paymentData.paymentType || '',
      amount: paymentData.amount || 0,
      advance: Number(
        typeof paymentData.advance === 'number'
          ? paymentData.advance
          : (paymentData.utilizedAdvance ?? paymentData.advanceAmount ?? 0)
      ) || 0,
      destinationAccount: paymentData.destinationAccount || paymentData.destinationAccountId || 0,
      destinationAccountName: paymentData.destinationAccountName || '',
      note: paymentData.note || '',
      advanceId: paymentData.advanceId || 0,
      branchId: paymentData.branchId || 0,
      ipAddress: paymentData.ipAddress || '',
    } as Payment
  }
}

export const paymentsService = new PaymentsService()

